// components/TopSkuBarChart.jsx

import { Card, Box, Typography } from "@mui/material";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    LabelList
} from "recharts";

export default function TopSkuBarChart({
    title,
    xLabels,
    series,
    dataMatrix,
    xTicks,
    valueFormatter = (v) => `$${v}M`
}) {
    // Build chartData
    const chartData = xLabels.map((label, i) => {
        const row = { sku: label };
        series.forEach((s, j) => {
            row[s.key] = dataMatrix.actual[i][j];
        });
        return row;
    });
    const chartWidth = Math.max(xTicks.length * 120, 900);

    return (
        <Card
            sx={{
                width: "100%",
                borderRadius: "12px !important",
                background: "#fff",
                overflow: "hidden",
                boxShadow: "0px 1px 12px rgba(0, 0, 0, 0.1)",
                position: "relative",
                overflow: "visible",
                height: "100%",
                p: 2
            }}
        >
            {/* TITLE BAR */}
            <Box
                sx={{
                    position: "absolute",
                    top: "-10px",
                    left: "12px",
                    right: "12px",
                    background: "#115f9c",
                    height: "36px",
                    borderRadius: "4px !important",
                    display: "flex",
                    alignItems: "center",
                    px: "12px",
                    color: "#fff",
                    fontSize: { xs: 12, sm: 12, md: 16 },
                    fontWeight: 200,
                    boxShadow: "0px 1px 16px rgba(128, 203, 255, 0.5)"
                }}
            >
                {title}
            </Box>

            {/* CHART */}
            <Box sx={{
                width: "100%",
                height: 480,
                overflowX: "auto",
                overflowY: "hidden",
            }}>
                <Box
                    sx={{
                        width: {
                            xs: chartWidth,   //  enable horizontal scroll on mobile
                            sm: chartWidth,   // enable horizontal scroll on tablet
                            md: chartWidth,
                            lg: "100%"
                        },
                        minWidth: {
                            xs: chartWidth,
                            sm: chartWidth,
                            md: chartWidth,
                            lg: "100%"
                        },
                        height: "100%",
                    }}
                >
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            layout="vertical"
                            data={chartData}
                            margin={{ top: 40, right: 50, left: -30, bottom: 10 }}
                        >
                            <CartesianGrid stroke="#1111110D" horizontal={false} />

                            <XAxis
                                type="number"
                                domain={[0, Math.max(...xTicks)]}
                                ticks={xTicks}
                                tick={{ fontSize: 10, color: "#111111", fontWeight: 200 }}

                            />

                            <YAxis
                                type="category"
                                dataKey="sku"
                                width={260}   // give enough space for the full text
                                tick={({ payload, x, y, textAnchor }) => (
                                    <text
                                        x={x}
                                        y={y}
                                        textAnchor={textAnchor}
                                        fontSize={10}
                                        fill="#11111180"
                                        // dy={4}
                                        style={{
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            fontWeight: 200,
                                            cursor:"pointer"
                                        }}
                                    >
                                        <title>{payload.value}</title>
                                        {payload.value.length > 36
                                            ? `${payload.value.slice(0, 36)}...`
                                            : payload.value}
                                    </text>
                                )}
                            />

                            {/* <Tooltip
                            formatter={(value) => `$${value}M`}
                            labelStyle={{ fontWeight: 600 }}
                        /> */}

                            {/* Bars = This Period + Last Period */}
                            {series.map((s) => (
                                <Bar
                                    key={s.key}
                                    dataKey={s.key}
                                    fill={s.color}
                                    barSize={14}
                                    radius={[0, 0, 0, 0]}
                                >
                                    {/* Right side labels ($280M etc.) */}
                                    <LabelList
                                        dataKey={s.key}
                                        position="right"
                                        formatter={valueFormatter}
                                        style={{ fill: "#11111180", fontSize: 8, fontWeight: 400 }}
                                    />
                                </Bar>
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                </Box>

            </Box>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    flexWrap: "wrap",
                    gap: 2,
                    mt: '16px'
                }}
            >
                {series.map((s) => (
                    <Box key={s.label} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                            sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                backgroundColor: s.color
                            }}
                        />
                        <Typography fontSize={10} style={{ color: "#111111", fontWeight: 200 }}>{s.label}</Typography>
                    </Box>
                ))}
            </Box>

        </Card>
    );
}
